/* eslint-disable max-lines */
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Dispatch, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { DynamicAudience } from 'src/generated/mloop-graphql';

import AtsSevice from 'src/hooks/atsService/AtsService';

import { getScheduleContent } from 'src/store/selectors/schedule-communications';
import { getScheduleUpdates } from 'src/store/selectors/schedule-update';
import { getScheduleById } from 'src/store/selectors/schedules';
import scheduleCommunicationsSlice, { ChatConversationInvitees } from 'src/store/slices/schedule-communications';
import { ScheduleUpdate } from 'src/store/slices/schedule-update';

import { AdditionalAttendeesByInterviewId, InterviewAdditionalAttendees } from 'src/utils/additionalAttendees';
import { SettingAttendeeEntry } from 'src/utils/settingAdditionalAttendees';
import getPlaceholders from 'src/utils/PlaceholderFiller/getPlaceholders';
import type { RequestBody as GetEventContentRequestBody } from 'src/utils/api/getEventContent';
import getEventContent from 'src/utils/api/getEventContent';
import { InterviewSchedule } from 'src/utils/api/getScheduleOptions';
import type { RequestBody as UpdateScheduleRequestBody } from 'src/utils/api/updateSchedule';
import htmlToMrkdwn from 'src/utils/htmlToMrkdwn';
import isQuillTextEmpty from 'src/utils/isQuillTextEmpty';
import logError from 'src/utils/logError';
import { plainText } from 'src/utils/plainText';

import { applyUpdatesToSchedule } from 'src/views-new/ScheduleFlow/Steps/Schedule/applyUpdatesToSchedule';
import { DefaultTemplateIdMap } from 'src/views-new/Settings/TemplateComposer/useTemplateQueries';

import type { AppThunk } from 'src/store';
import { State } from 'src/store';

export type IdToNullableIdMap = { [key: string]: string | null };

export interface CalendarTemplate {
  templateID: string | null;
  subject: string;
  body: string;
}

export interface CandidateAvailability {
  id: string; // can be removed
  day: string; // deprecated
  startAt: string;
  endAt: string;
}

export interface Update {
  selectedToEmailAddress: string;
  candidateTemplateID: string;
  candidateEventTemplateID: string;
  interviewerTemplateIDs: IdToNullableIdMap; // jobStageInterviewId/slotId key
  hasSameContent: boolean;
  candidateCalendarId: string;
  interviewerCalendarId: string;
  slackChannelEnabled: boolean;
  slackChannelId: string;
  slackChannelName: string;
  selectedScheduleId: string | null;
  toEmailAddresses: string[];
  initialEmailSubject: string | null;
  initialEmailBody: string | null;
  candidateGoogleEventId: string | null;
  isPrivateCalendarEvent: boolean;
  oldContentRequest: {
    remoteCalendarId: string;
    remoteEventId: string;
  }[];
}

interface SchedulingState {
  stepCandidateAvailability: {
    loading: boolean;
    submitting: boolean;
    error: string | null;
  };
  stepInterviewPlan: {
    loading: boolean;
    submitting: boolean;
    error: string | null;
  };
  stepScheduleOptions: {
    loading: boolean;
    submitting: boolean;
    error: string | null;
    shouldRefreshOptions: boolean;
  };
  stepScheduleContent: {
    loading: boolean;
    isWaiting: boolean;
    isSubmitting: boolean;
    error: string | null;
  };
  selectedScheduleId: string | null;
  isPrivateCalendarEvent: boolean;
  originalCalendarPrivacy: boolean;
  selectedToEmailAddress: string | null;
  toEmailAddresses: string[];
  ccEmployeeIDs: string[];
  bccEmployeeIDs: string[];
  ccExternalAddresses: string[];
  bccExternalAddresses: string[];
  ccDynamicAudience: DynamicAudience[];
  bccDynamicAudience: DynamicAudience[];
  candidateTemplateID: string | null;
  candidateCalendarTemplateID: string | null;
  interviewerTemplateIDs: IdToNullableIdMap; // jobStageInterviewId/slotId key
  initialInterviewerEventTemplateID: string | null;
  hasSameContent: boolean;
  candidateCommunicationsEnabled: boolean;
  candidateEventCommunicationsEnabled: boolean;
  candidateCalendarId: string | null;
  interviewerCalendarId: string | null;
  // Slack Channel & First Message
  slackChannelEnabled: boolean;
  slackChannelId: string;
  slackChannelName: string;
  prevSlackChannelConversationId: string | undefined;
  prevSlackChannelName: string | undefined;
  slackChannelMessageTemplateID: string | null;
  slackChannelRecruitingTeamEmployeeIDs: string[];
  slackChannelHiringManagerEmployeeIDs: string[];
  chatConversationInvitees: ChatConversationInvitees;
  originalCandidateCalendarId: string;
  originalInterviewerCalendarId: string;
  candidateEmailOldContent: OldContent | null;
  originalCandidateGoogleEventId: string | null;
  eventOldContent: OldContent[];
  updateFlowInterviewExpanded: string[];
  // jobStageSettings added here instead of in scheduling-comms store b/c we don't have a scheduleId to use for adding items to that store
  jobStageSettings: {
    interviewerCalendarId: string | null;
    candidateCalendarId: string | null;
    interviewerEventTemplateId: string | null;
  };
  // Resolved org/job-stage additional-attendee defaults, per entry: each configured recipient
  // carries its own role, and a configured audience carries the role its members inherit.
  candidateSettingAttendees: SettingAttendeeEntry[];
  interviewerSettingAttendees: SettingAttendeeEntry[];
  debriefSettingAttendees: SettingAttendeeEntry[];
  // Update flow only: each existing interview's stored additional-attendee snapshot, keyed by
  // application-stage interview id. Seeds that interview's draft list and is the baseline the
  // update payload diffs against so an untouched row can omit the field.
  interviewerAdditionalAttendeeSnapshots: AdditionalAttendeesByInterviewId;
  // Update flow only: the candidate event's stored snapshot, or null when it has none. Undefined
  // means "not loaded", which is how the create flow stays on the settings seed.
  candidateAdditionalAttendeeSnapshot: InterviewAdditionalAttendees | null | undefined;
}

export interface OldContent {
  googleEventId: string | null;
  oldSummary: string;
  oldDescription: string;
}

interface InitializeLastUsedTemplatesPayload {
  slackTemplateId: string | null;
  candidateEmailTemplateId: string | null;
  candidateEventTemplateId: string | null;
  interviewerEventTemplateId: string | null;
}

const getInitialState = (): SchedulingState => {
  return {
    stepCandidateAvailability: {
      loading: true,
      submitting: false,
      error: '',
    },
    stepInterviewPlan: {
      loading: true,
      submitting: false,
      error: '',
    },
    stepScheduleOptions: {
      loading: true,
      submitting: false,
      error: '',
      shouldRefreshOptions: false,
    },
    stepScheduleContent: {
      loading: true,
      isWaiting: false, // Whether or not we are waiting on an API call
      isSubmitting: false, // Whether or not we are submitting the final API call to create/update
      error: '',
    },
    selectedScheduleId: null,
    isPrivateCalendarEvent: false,
    originalCalendarPrivacy: false,
    selectedToEmailAddress: null,
    toEmailAddresses: [],
    ccEmployeeIDs: [],
    bccEmployeeIDs: [],
    ccExternalAddresses: [],
    bccExternalAddresses: [],
    ccDynamicAudience: [],
    bccDynamicAudience: [],
    candidateTemplateID: null, // email template id
    candidateCalendarTemplateID: null,
    interviewerTemplateIDs: {},
    initialInterviewerEventTemplateID: '',
    hasSameContent: true,
    candidateCommunicationsEnabled: true,
    candidateEventCommunicationsEnabled: true,
    candidateCalendarId: '',
    interviewerCalendarId: '',
    // Slack Channel & First Message
    slackChannelEnabled: false,
    slackChannelId: '',
    slackChannelName: '',
    prevSlackChannelConversationId: undefined,
    prevSlackChannelName: '',
    slackChannelMessageTemplateID: null,
    slackChannelRecruitingTeamEmployeeIDs: [],
    slackChannelHiringManagerEmployeeIDs: [],
    chatConversationInvitees: {
      employeeIds: [],
      dynamicAudiences: [],
    },
    originalCandidateCalendarId: '',
    originalInterviewerCalendarId: '',
    candidateEmailOldContent: null,
    originalCandidateGoogleEventId: null,
    eventOldContent: [],
    updateFlowInterviewExpanded: [],
    jobStageSettings: {
      candidateCalendarId: null,
      interviewerCalendarId: null,
      interviewerEventTemplateId: null,
    },
    candidateSettingAttendees: [],
    interviewerSettingAttendees: [],
    debriefSettingAttendees: [],
    interviewerAdditionalAttendeeSnapshots: {},
    candidateAdditionalAttendeeSnapshot: undefined,
  };
};

function clearErrors(state: SchedulingState) {
  state.stepCandidateAvailability.error = '';
  state.stepInterviewPlan.error = '';
  state.stepScheduleOptions.error = '';
  state.stepScheduleContent.error = '';
}

const slice = createSlice({
  name: 'scheduling',
  initialState: getInitialState(),
  reducers: {
    /// /////////////////
    // Initialization //
    /// /////////////////

    clear(/* state: SchedulingState, action: PayloadAction<{}> */) {
      return getInitialState();
    },

    clearAnyErrors(state: SchedulingState /* , action: PayloadAction<{}> */) {
      clearErrors(state);
    },

    /// //////////////////////////////
    // Candidate Availability Step //
    /// //////////////////////////////

    setCandidateAvailbilityStepError(state: SchedulingState, action: PayloadAction<{ error: string | null }>) {
      const { error } = action.payload;
      state.stepCandidateAvailability.error = error;
    },

    setCandidateAvailbilityStepSubmitting(state: SchedulingState, action: PayloadAction<{ submitting: boolean }>) {
      const { submitting } = action.payload;
      state.stepCandidateAvailability.submitting = submitting;
      state.stepInterviewPlan.loading = submitting;
    },

    /// //////////////////////
    // Interview Plan Step //
    /// //////////////////////

    setInterviewPlanStepError(state: SchedulingState, action: PayloadAction<{ error: string | null }>) {
      const { error } = action.payload;
      state.stepInterviewPlan.error = error;
    },

    clearInterviewPlanStepError(state: SchedulingState) {
      state.stepInterviewPlan.error = '';
    },

    setInterviewPlanStepSubmitting(state: SchedulingState, action: PayloadAction<{ submitting: boolean }>) {
      const { submitting } = action.payload;
      state.stepInterviewPlan.submitting = submitting;
      state.stepScheduleOptions.loading = submitting;
    },

    /// ////////////////////////
    // Schedule Options Step //
    /// ////////////////////////

    setScheduleOptionsStepError(state: SchedulingState, action: PayloadAction<{ error: string | null }>) {
      const { error } = action.payload;
      state.stepScheduleOptions.error = error;
    },

    setScheduleOptionsStepSubmitting(state: SchedulingState, action: PayloadAction<{ submitting: boolean }>) {
      const { submitting } = action.payload;
      state.stepScheduleOptions.submitting = submitting;
      state.stepScheduleContent.loading = submitting;
    },

    setScheduleOptionsSetupRefresh(state: SchedulingState, action: PayloadAction<{ refresh: boolean }>) {
      const { refresh } = action.payload;
      state.stepScheduleOptions.shouldRefreshOptions = refresh;
    },

    clearSelectedScheduleId(state: SchedulingState) {
      state.selectedScheduleId = null;
    },

    setSelectedScheduleId(state: SchedulingState, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      state.selectedScheduleId = id;
    },

    /// ////////////
    // Send Step //
    /// ////////////

    setScheduleContentStepError(state: SchedulingState, action: PayloadAction<{ error: string | null }>) {
      const { error } = action.payload;
      state.stepScheduleContent.error = error;
    },

    setScheduleContentStepSubmitting(state: SchedulingState) {
      state.stepScheduleContent.isSubmitting = true;
      state.stepScheduleContent.isWaiting = true;
    },

    clearScheduleContentStepSubmitting(state: SchedulingState) {
      state.stepScheduleContent.isSubmitting = false;
      state.stepScheduleContent.isWaiting = false;
    },

    setScheduleContentStepWaiting(state: SchedulingState, action: PayloadAction<{ isWaiting: boolean }>) {
      const { isWaiting } = action.payload;
      state.stepScheduleContent.isWaiting = isWaiting;
    },

    updateInterviewerTemplateIDs(
      state: SchedulingState,
      action: PayloadAction<{ interviewerTemplateIDs: IdToNullableIdMap }>
    ) {
      const { interviewerTemplateIDs } = action.payload;
      state.interviewerTemplateIDs = interviewerTemplateIDs;
    },

    updateCandidateCalendarId(state: SchedulingState, action: PayloadAction<{ candidateCalendarId: string }>) {
      const { candidateCalendarId } = action.payload;
      state.candidateCalendarId = candidateCalendarId;
    },

    updateInterviewerCalendarId(state: SchedulingState, action: PayloadAction<{ interviewerCalendarId: string }>) {
      const { interviewerCalendarId } = action.payload;
      state.interviewerCalendarId = interviewerCalendarId;
    },

    updateOriginalCalendarPrivacy(state: SchedulingState, action: PayloadAction<{ isPrivate: boolean }>) {
      const { isPrivate } = action.payload;
      state.originalCalendarPrivacy = isPrivate;
    },

    updateOriginalCandidateCalendarId(state: SchedulingState, action: PayloadAction<{ candidateCalendarId: string }>) {
      const { candidateCalendarId } = action.payload;
      state.originalCandidateCalendarId = candidateCalendarId;
    },

    updateOriginalInterviewerCalendarId(
      state: SchedulingState,
      action: PayloadAction<{ interviewerCalendarId: string }>
    ) {
      const { interviewerCalendarId } = action.payload;
      state.originalInterviewerCalendarId = interviewerCalendarId;
    },

    updateHasSameContent(state: SchedulingState, action: PayloadAction<{ hasSameContent: boolean }>) {
      const { hasSameContent } = action.payload;
      state.hasSameContent = hasSameContent;
    },

    updateSlackChannelName(state: SchedulingState, action: PayloadAction<{ value: string }>) {
      const { value } = action.payload;
      state.slackChannelName = value;
    },

    updatePrevSlackChannelInfo(
      state: SchedulingState,
      action: PayloadAction<{ conversationId: string; value: string }>
    ) {
      const { conversationId, value } = action.payload;

      state.prevSlackChannelConversationId = conversationId;
      state.prevSlackChannelName = value;
    },

    initCommunicationTemplateIds(state: SchedulingState, action: PayloadAction<InitializeLastUsedTemplatesPayload>) {
      const { slackTemplateId, candidateEmailTemplateId, candidateEventTemplateId, interviewerEventTemplateId } =
        action.payload;
      state.candidateTemplateID = candidateEmailTemplateId || null;
      state.candidateCalendarTemplateID = candidateEventTemplateId || null;
      state.slackChannelMessageTemplateID = slackTemplateId || null;
      // This is a giant hack, but its one we are already using. We can fix this
      // and key interviewer communications by jobStageId when we move it to its own slice
      state.initialInterviewerEventTemplateID = interviewerEventTemplateId || null;
    },

    initInterviewerTemplateIds(state: SchedulingState, action: PayloadAction<{ schedule: InterviewSchedule }>) {
      const { schedule } = action.payload;
      schedule.events?.forEach((event) => {
        const { slotId } = event;
        if (slotId && !state.interviewerTemplateIDs[slotId]) {
          state.interviewerTemplateIDs[slotId] = state.initialInterviewerEventTemplateID;
        }
      });
    },

    updateCandidateTemplateID(state: SchedulingState, action: PayloadAction<{ templateID: string }>) {
      const { templateID } = action.payload;
      state.candidateTemplateID = templateID;
    },

    updateInterviewerTemplateID(state: SchedulingState, action: PayloadAction<{ slotId: string; templateID: string }>) {
      const { slotId, templateID } = action.payload;
      state.interviewerTemplateIDs[slotId] = templateID;
    },

    updateCandidateCalendarTemplateID: (state, action: PayloadAction<{ templateID: string }>) => {
      const { templateID } = action.payload;
      state.candidateCalendarTemplateID = templateID;
    },

    addUpdateFlowInterviewExpanded: (state, action: PayloadAction<{ interviewId: string }>) => {
      const { interviewId } = action.payload;
      if (!state.updateFlowInterviewExpanded.includes(interviewId)) {
        state.updateFlowInterviewExpanded.push(interviewId);
      }
    },

    updateCCEmployeeIDs(state: SchedulingState, action: PayloadAction<{ employeeIDs: string[] }>) {
      const { employeeIDs } = action.payload;
      state.ccEmployeeIDs = employeeIDs || [];
    },

    updateBCCEmployeeIDs(state: SchedulingState, action: PayloadAction<{ employeeIDs: string[] }>) {
      const { employeeIDs } = action.payload;
      state.bccEmployeeIDs = employeeIDs || [];
    },

    updateCCExternalAddresses(state: SchedulingState, action: PayloadAction<{ ccs: string[] }>) {
      const { ccs } = action.payload;
      state.ccExternalAddresses = ccs || [];
    },

    updateBCCExternalAddresses(state: SchedulingState, action: PayloadAction<{ bccs: string[] }>) {
      const { bccs } = action.payload;
      state.bccExternalAddresses = bccs || [];
    },

    updateCCDynamicAudience(state: SchedulingState, action: PayloadAction<{ ccDynamicAudience: DynamicAudience[] }>) {
      const { ccDynamicAudience } = action.payload;
      state.ccDynamicAudience = ccDynamicAudience || [];
    },

    updateBCCDynamicAudience(state: SchedulingState, action: PayloadAction<{ bccDynamicAudience: DynamicAudience[] }>) {
      const { bccDynamicAudience } = action.payload;
      state.bccDynamicAudience = bccDynamicAudience || [];
    },

    updateCandidateCommunicationsEnabled(state: SchedulingState, action: PayloadAction<{ enabled: boolean }>) {
      const { enabled } = action.payload;
      state.candidateCommunicationsEnabled = enabled;
    },

    updateCandidateEventCommunicationsEnabled(state: SchedulingState, action: PayloadAction<{ enabled: boolean }>) {
      const { enabled } = action.payload;
      state.candidateEventCommunicationsEnabled = enabled;
    },

    updateSlackChannelEnabled(state: SchedulingState, action: PayloadAction<{ enabled: boolean }>) {
      const { enabled } = action.payload;
      state.slackChannelEnabled = enabled;
    },

    updateSlackChannelId(state: SchedulingState, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      state.slackChannelId = id;
    },

    updateIsPrivateCalendarEvent(state: SchedulingState, action: PayloadAction<{ isPrivate: boolean }>) {
      const { isPrivate: enabled } = action.payload;
      state.isPrivateCalendarEvent = enabled;
    },

    setJobStageDefaultAdditionalAttendees(
      state: SchedulingState,
      action: PayloadAction<{
        candidate?: SettingAttendeeEntry[];
        interviewer?: SettingAttendeeEntry[];
        debrief?: SettingAttendeeEntry[];
      }>
    ) {
      const { candidate, interviewer, debrief } = action.payload;
      state.candidateSettingAttendees = candidate ?? [];
      state.interviewerSettingAttendees = interviewer ?? [];
      state.debriefSettingAttendees = debrief ?? [];
    },

    setInterviewerAdditionalAttendeeSnapshots(
      state: SchedulingState,
      action: PayloadAction<{ snapshots: AdditionalAttendeesByInterviewId }>
    ) {
      state.interviewerAdditionalAttendeeSnapshots = action.payload.snapshots;
    },

    setCandidateAdditionalAttendeeSnapshot(
      state: SchedulingState,
      action: PayloadAction<{ snapshot: InterviewAdditionalAttendees | null | undefined }>
    ) {
      state.candidateAdditionalAttendeeSnapshot = action.payload.snapshot;
    },

    updateCandidateEmailOldContent(
      state: SchedulingState,
      action: PayloadAction<{ initialEmailSubject: string; initialEmailBody: string }>
    ) {
      const { initialEmailSubject, initialEmailBody } = action.payload;
      state.candidateEmailOldContent = {
        googleEventId: null,
        oldSummary: initialEmailSubject,
        oldDescription: initialEmailBody,
      };
    },

    updateOriginalCandidateGoogleEventId(
      state: SchedulingState,
      action: PayloadAction<{ originalCandidateGoogleEventId: string | null }>
    ) {
      const { originalCandidateGoogleEventId } = action.payload;
      state.originalCandidateGoogleEventId = originalCandidateGoogleEventId;
    },

    updateEventOldContent(state: SchedulingState, action: PayloadAction<{ oldContent: OldContent[] }>) {
      const { oldContent } = action.payload;
      state.eventOldContent = oldContent;
    },

    updateSlackChannelRecruitingTeamEmployeeIDs(
      state: SchedulingState,
      action: PayloadAction<{ employeeIDs: string[] }>
    ) {
      const { employeeIDs } = action.payload;
      state.slackChannelRecruitingTeamEmployeeIDs = employeeIDs || [];
    },

    updateSlackChannelHiringManagerEmployeeIDs(
      state: SchedulingState,
      action: PayloadAction<{ employeeIDs: string[] }>
    ) {
      const { employeeIDs } = action.payload;
      state.slackChannelHiringManagerEmployeeIDs = employeeIDs || [];
    },

    updateChatConversationInvitees(state: SchedulingState, action: PayloadAction<ChatConversationInvitees>) {
      state.chatConversationInvitees = action.payload;
    },

    updateToEmailAddress(state: SchedulingState, action: PayloadAction<{ toEmailAddress: string }>) {
      const { toEmailAddress } = action.payload;
      state.selectedToEmailAddress = toEmailAddress;
    },

    updateToEmailAddresses(state: SchedulingState, action: PayloadAction<{ toEmailAddresses: string[] }>) {
      const { toEmailAddresses } = action.payload;
      state.toEmailAddresses = toEmailAddresses;
    },

    setStepCandidateAvailabilityLoading(state: SchedulingState, action: PayloadAction<{ loading: boolean }>) {
      const { loading } = action.payload;
      state.stepCandidateAvailability.loading = loading;
    },
  },
});

/// /////////////////
// Update //
/// /////////////////

const getOldEventContent = async (oldContentRequest: GetEventContentRequestBody): Promise<OldContent[]> => {
  try {
    const response = await getEventContent(oldContentRequest);
    if (!response.events) {
      return [];
    }
    return response.events.map((event) => {
      return {
        googleEventId: event.remote_id,
        oldSummary: event.title,
        oldDescription: plainText(event.description),
      };
    });
  } catch (error) {
    logError(error);
    console.log(error?.response);
  }
  return [];
};

export const updateIsPrivateCalendarEvent =
  (enabled: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateIsPrivateCalendarEvent({ isPrivate: enabled }));
  };

export const setJobStageDefaultAdditionalAttendees =
  (args: {
    candidate?: SettingAttendeeEntry[];
    interviewer?: SettingAttendeeEntry[];
    debrief?: SettingAttendeeEntry[];
  }): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.setJobStageDefaultAdditionalAttendees(args));
  };

export const setInterviewerAdditionalAttendeeSnapshots =
  (snapshots: AdditionalAttendeesByInterviewId): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.setInterviewerAdditionalAttendeeSnapshots({ snapshots }));
  };

export const setCandidateAdditionalAttendeeSnapshot =
  (snapshot: InterviewAdditionalAttendees | null | undefined): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.setCandidateAdditionalAttendeeSnapshot({ snapshot }));
  };

export const setupUpdate =
  (update: Update): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateInterviewerTemplateIDs({ interviewerTemplateIDs: update.interviewerTemplateIDs }));
    dispatch(slice.actions.updateCandidateTemplateID({ templateID: update.candidateTemplateID }));
    dispatch(slice.actions.updateCandidateCalendarTemplateID({ templateID: update.candidateEventTemplateID }));
    dispatch(slice.actions.updateCandidateCalendarId({ candidateCalendarId: update.candidateCalendarId }));
    dispatch(slice.actions.updateInterviewerCalendarId({ interviewerCalendarId: update.interviewerCalendarId }));
    dispatch(slice.actions.updateOriginalCandidateCalendarId({ candidateCalendarId: update.candidateCalendarId }));
    dispatch(slice.actions.updateIsPrivateCalendarEvent({ isPrivate: update.isPrivateCalendarEvent }));
    dispatch(slice.actions.updateOriginalCalendarPrivacy({ isPrivate: update.isPrivateCalendarEvent }));
    dispatch(
      slice.actions.updateOriginalCandidateGoogleEventId({
        originalCandidateGoogleEventId: update.candidateGoogleEventId,
      })
    );
    dispatch(
      slice.actions.updateOriginalInterviewerCalendarId({ interviewerCalendarId: update.interviewerCalendarId })
    );

    dispatch(slice.actions.updateSlackChannelId({ id: update.slackChannelId }));
    dispatch(slice.actions.updateSlackChannelEnabled({ enabled: update.slackChannelEnabled }));
    dispatch(slice.actions.updateSlackChannelName({ value: update.slackChannelName }));
    dispatch(slice.actions.updateToEmailAddress({ toEmailAddress: update.selectedToEmailAddress }));
    dispatch(slice.actions.updateToEmailAddresses({ toEmailAddresses: update.toEmailAddresses }));

    // Set old content
    const oldContent = await getOldEventContent({ calendarEventIds: update.oldContentRequest });
    dispatch(slice.actions.updateEventOldContent({ oldContent }));
  };

/// /////////////////
// Initialization //
/// /////////////////

export const { reducer } = slice;

export const clear = (): AppThunk => async (dispatch) => {
  dispatch(slice.actions.clear());
};

/// //////////////////////////////
// Candidate Availability Step //
/// //////////////////////////////

export const initCommunicationTemplateIds =
  (defaultTemplateIdMap: DefaultTemplateIdMap): AppThunk =>
  async (dispatch) => {
    dispatch(
      slice.actions.initCommunicationTemplateIds({
        candidateEmailTemplateId: defaultTemplateIdMap.CANDIDATE_CONFIRMATION || null,
        candidateEventTemplateId: defaultTemplateIdMap.CANDIDATE_INVITE || null,
        interviewerEventTemplateId: defaultTemplateIdMap.INTERVIEWER_INVITE || null,
        slackTemplateId: defaultTemplateIdMap.SLACK_CHANNEL_MESSAGE || null,
      })
    );
  };

/// //////////////////////
// Interview Plan Step //
/// //////////////////////

export const clearInterviewPlanStepError = (): AppThunk => async (dispatch) =>
  dispatch(slice.actions.clearInterviewPlanStepError());

/// ////////////////////////
// Schedule Options Step //
/// ////////////////////////
export const clearSelectedScheduleId = (): AppThunk => async (dispatch) =>
  dispatch(slice.actions.clearSelectedScheduleId());

export const setSelectedScheduleId =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.setSelectedScheduleId({ id }));
  };

export const submitScheduleOptionsStep =
  (applicationId: string, selectedScheduleId: string, incrementStp = true, gotoNextStep?: () => void): AppThunk =>
  async (dispatch, getState) => {
    dispatch(slice.actions.setScheduleOptionsStepSubmitting({ submitting: true }));

    try {
      const { scheduling, schedules } = getState();

      const scheduleId = selectedScheduleId ?? scheduling.selectedScheduleId;

      if (!scheduleId) {
        dispatch(slice.actions.setScheduleOptionsStepError({ error: 'No schedule selected' }));
        return;
      }
      // Initialize interviewer template ids before content regeneration
      dispatch(slice.actions.initInterviewerTemplateIds({ schedule: schedules.byId[scheduleId] }));

      // Set or update interviewer ids for slack channel
      const schedule = getScheduleById(getState(), scheduleId);
      const scheduleUpdates = getScheduleUpdates(getState(), schedule.id);
      const updatedSchedule = applyUpdatesToSchedule(schedule, scheduleUpdates);
      let interviewerIDs: string[] = [];
      if (updatedSchedule && updatedSchedule.events) {
        interviewerIDs = _.uniq(
          updatedSchedule.events.map((e) => e.interviewers?.map((i) => i.employee.id) || []).flat()
        );
      }
      dispatch(
        scheduleCommunicationsSlice.actions.updateSlackChannelInterviewerEmployeeIDs({
          scheduleId,
          employeeIDs: interviewerIDs,
        })
      );

      if (scheduling.slackChannelHiringManagerEmployeeIDs) {
        dispatch(
          scheduleCommunicationsSlice.actions.updateSlackChannelHiringManagerEmployeeIDs({
            scheduleId,
            employeeIDs: scheduling.slackChannelHiringManagerEmployeeIDs,
          })
        );
      }

      if (
        scheduling.chatConversationInvitees.employeeIds.length ||
        scheduling.chatConversationInvitees.dynamicAudiences.length
      ) {
        dispatch(
          scheduleCommunicationsSlice.actions.updateChatConversationInvitees({
            scheduleId,
            invitees: scheduling.chatConversationInvitees,
          })
        );
      }

      if (scheduling.slackChannelRecruitingTeamEmployeeIDs) {
        dispatch(
          scheduleCommunicationsSlice.actions.updateSlackChannelRecruitingTeamEmployeeIDs({
            scheduleId,
            employeeIDs: scheduling.slackChannelRecruitingTeamEmployeeIDs,
          })
        );
      }

      dispatch(
        scheduleCommunicationsSlice.actions.setFlow({
          scheduleId,
          applicationId,
          isDebrief: false,
        })
      );

      dispatch(
        scheduleCommunicationsSlice.actions.updatePrevSlackChannelName({
          scheduleId: scheduling.selectedScheduleId || '',
          name: scheduling.prevSlackChannelName || '',
        })
      );
      dispatch(
        scheduleCommunicationsSlice.actions.updateConversationId({
          scheduleId,
          conversationId: scheduling.prevSlackChannelConversationId || '',
        })
      );
      dispatch(
        scheduleCommunicationsSlice.actions.updateSlackChannelEnabled({
          scheduleId: scheduling.selectedScheduleId || '',
          isEnabled: scheduling.slackChannelEnabled,
        })
      );

      dispatch(
        scheduleCommunicationsSlice.actions.updateSlackChannelMessageTemplateID({
          scheduleId: scheduling.selectedScheduleId || '',
          templateId: scheduling.slackChannelMessageTemplateID || '',
        })
      );

      if (incrementStp) {
        if (gotoNextStep) {
          gotoNextStep();
        }
      }
      dispatch(slice.actions.setScheduleOptionsStepError({ error: null }));
    } catch (error) {
      logError(error);
      console.log(error?.response);
      dispatch(slice.actions.setScheduleOptionsStepError({ error: getExternalErrorMessage(error) }));
    }
    dispatch(slice.actions.setScheduleOptionsStepSubmitting({ submitting: false }));
  };

/// //////////////////////
// Send / Content Step //
/// //////////////////////
export const updatePrevSlackChannelInfo =
  (conversationId: string, value: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updatePrevSlackChannelInfo({ conversationId, value }));
  };

export const updateCCEmployeeIDs =
  (employeeIDs: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCCEmployeeIDs({ employeeIDs }));
  };

export const updateBCCEmployeeIDs =
  (employeeIDs: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateBCCEmployeeIDs({ employeeIDs }));
  };

export const updateCCExternalAddresses =
  (ccs: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCCExternalAddresses({ ccs }));
  };

export const updateBCCExternalAddresses =
  (bccs: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateBCCExternalAddresses({ bccs }));
  };

export const updateCCDynamicAudience =
  (ccDynamicAudience: DynamicAudience[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCCDynamicAudience({ ccDynamicAudience }));
  };

export const updateBCCDynamicAudience =
  (bccDynamicAudience: DynamicAudience[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateBCCDynamicAudience({ bccDynamicAudience }));
  };

export const updateCandidateCommunicationsEnabled =
  (enabled: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCandidateCommunicationsEnabled({ enabled }));
  };

export const updateCandidateEventCommunicationsEnabled =
  (enabled: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCandidateEventCommunicationsEnabled({ enabled }));
  };

export const updateSlackChannelEnabled =
  (enabled: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateSlackChannelEnabled({ enabled }));
  };

export const updateSlackChannelRecruitingTeamEmployeeIDs =
  (employeeIDs: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateSlackChannelRecruitingTeamEmployeeIDs({ employeeIDs }));
  };

export const updateSlackChannelHiringManagerEmployeeIDs =
  (employeeIDs: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateSlackChannelHiringManagerEmployeeIDs({ employeeIDs }));
  };

export const updateChatConversationInvitees =
  (invitees: ChatConversationInvitees): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateChatConversationInvitees(invitees));
  };

export const updateHasSameContent =
  (hasSameContent: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateHasSameContent({ hasSameContent }));
  };

export const updateToEmailAddress =
  (toEmailAddress: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateToEmailAddress({ toEmailAddress }));
  };

export const updateToEmailAddresses =
  (toEmailAddresses: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateToEmailAddresses({ toEmailAddresses }));
  };

export const addUpdateFlowInterviewExpanded =
  (interviewId: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.addUpdateFlowInterviewExpanded({ interviewId }));
  };

export const updateCandidateCalendarId =
  (candidateCalendarId: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCandidateCalendarId({ candidateCalendarId }));
  };

export const updateCandidateCalendarTemplateID =
  (templateID: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCandidateCalendarTemplateID({ templateID }));
  };

export const updateInterviewerCalendarId =
  (interviewerCalendarId: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateInterviewerCalendarId({ interviewerCalendarId }));
  };

export const updateCandidateTemplateID =
  (templateID: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateCandidateTemplateID({ templateID }));
  };

export const updateAllInterviewerTemplateID =
  (templateID: string): AppThunk =>
  async (dispatch, getState) => {
    const { interviewerTemplateIDs, selectedScheduleId } = getState().scheduling;
    const { scheduleUpdate } = getState();

    Object.keys(interviewerTemplateIDs).forEach((slotId) => {
      dispatch(slice.actions.updateInterviewerTemplateID({ slotId, templateID }));
    });

    if (selectedScheduleId && scheduleUpdate.byId[selectedScheduleId]) {
      scheduleUpdate.byId[selectedScheduleId].forEach((update) => {
        if (update.type !== 'ScheduleUpdateNewInterview') {
          return;
        }

        dispatch(slice.actions.updateInterviewerTemplateID({ slotId: update.applicationStageInterviewId, templateID }));
      });
    }
  };

export const updateInterviewerTemplateID =
  (eventId: string, slotId: string, templateID: string): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateInterviewerTemplateID({ slotId, templateID }));
  };

export const setScheduleOptionsSetupRefresh =
  (refresh: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch(slice.actions.setScheduleOptionsSetupRefresh({ refresh }));
  };

/// ////////////////////////
// Schedule Content Step //
/// ////////////////////////
export const getSubmitErrorMessage = (state: State, internalOnly = false): string | null => {
  const { scheduling, scheduleCommunications } = state;
  const { selectedScheduleId } = scheduling;
  const scheduleContent = getScheduleContent(state);

  // Slack channel option needs message content
  if (selectedScheduleId) {
    const scheduleCommsData = scheduleCommunications.byId[selectedScheduleId];
    if (scheduleCommsData.slackChannelEnabled && !htmlToMrkdwn(scheduleCommsData?.slackChannelContent || '')) {
      return 'Missing Slack channel message content';
    }
  }

  if (!internalOnly) {
    if (scheduling.candidateCommunicationsEnabled) {
      const isEmailEventSubjectEmpty = isQuillTextEmpty(scheduleContent?.candidateEventContent.summary || '');
      const isEmailEventBodyEmpty = isQuillTextEmpty(scheduleContent?.candidateEventContent.description || '');
      if (isEmailEventSubjectEmpty && isEmailEventBodyEmpty) {
        return 'Missing candidate email subject line and body text';
      }
      if (isEmailEventBodyEmpty) {
        return 'Missing candidate email body text';
      }
      if (isEmailEventSubjectEmpty) {
        return 'Missing candidate email subject line';
      }
    }

    if (!scheduling.hasSameContent && scheduling.candidateEventCommunicationsEnabled) {
      const isCalendarEventSubjectEmpty = isQuillTextEmpty(
        scheduleContent?.candidateCalendarEventContent.subject || ''
      );
      const isCalendarEventBodyEmpty = isQuillTextEmpty(scheduleContent?.candidateCalendarEventContent.body || '');
      if (isCalendarEventSubjectEmpty && isCalendarEventBodyEmpty) {
        return 'Missing candidate event subject line and body text';
      }
      if (isCalendarEventBodyEmpty) {
        return 'Missing candidate event body text';
      }
      if (isCalendarEventSubjectEmpty) {
        return 'Missing candidate event subject line';
      }
    }

    if (!scheduling.candidateCalendarId && !scheduling.interviewerCalendarId) {
      return 'Please confirm your event calendars from the calendar section.';
    }

    if (!scheduling.candidateCalendarId) {
      return 'Please confirm your candidate event calendar from the calendar section.';
    }
  }

  // interviewerCalendarId check is always needed
  if (!scheduling.interviewerCalendarId) {
    return 'Please confirm your interviewer event calendar from the calendar section.';
  }

  return null;
};

export const validateUpdateRequest = (data: UpdateScheduleRequestBody, candidateCommsEnabled: boolean) => {
  const interviewsMissingInterviewers = data.interviewerContents
    .filter((content) => content.interviewer.length === 0)
    .map((content) => {
      return content.interviewName;
    });
  if (interviewsMissingInterviewers.length !== 0) {
    throw new Error(`Following interviews are missing interviewers: ${interviewsMissingInterviewers.join(' ')}`);
  }
  if (candidateCommsEnabled && !data.candidateCalendarId) {
    throw new Error(`Missing Candidate Calendar id`);
  }
};

export const validateCandidateComms = (
  toEmailAddress: string,
  candidateCommsEnabled: boolean,
  candidateEventCommsEnabled: boolean
) => {
  if (candidateCommsEnabled && toEmailAddress === '') {
    throw new Error(`Missing Candidate email address`);
  }
  if (candidateEventCommsEnabled && toEmailAddress === '') {
    throw new Error(`Missing Candidate email address`);
  }
};

export const validSlackChannelName = (slackChannelName: string | undefined) => {
  const regexExp =
    /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/gi;
  if (slackChannelName && regexExp.test(slackChannelName)) {
    throw new Error(`Invlaid emoji char in Slack channel name`);
  }
};

export const validateNoTokens = (
  content: UpdateScheduleRequestBody,
  candidateCommsEnabled: boolean,
  atsService: AtsSevice
) => {
  let matches;

  if (candidateCommsEnabled) {
    matches = getPlaceholders(content.candidateEventContent?.summary || '');
    if (matches.length > 0) {
      throw new Error(`Found unused tokens in candidate email title: ${matches.join(' ')}`);
    }

    matches = getPlaceholders(content.candidateEventContent?.description || '');
    if (matches.length > 0) {
      throw new Error(`Found unused tokens in candidate email body: ${matches.join(' ')}`);
    }
  }

  for (const interviewerContent of content.interviewerContents) {
    matches = getPlaceholders(interviewerContent.summary);
    if (matches.length > 0) {
      throw new Error(`Found unused tokens in interviewer email title: ${matches.join(' ')}`);
    }

    matches = getPlaceholders(interviewerContent.description) as string[];
    const allowedUnusedTokens = ['{{INTERVIEW_KIT_LINK}}', '{{BRIGHTHIRE_GUIDE_LINK}}'];
    const isValidEventBody = matches.every((match) => {
      if (match === '{{INTERVIEW_KIT_LINK}}') {
        return atsService.skipInterviewKitLinkTokenValidation;
      }
      return allowedUnusedTokens.includes(match);
    });
    if (!isValidEventBody) {
      throw new Error(`Found unused tokens in interviewer email body: ${matches.join(' ')}`);
    }
  }
};

export function genDeletedInterviews(originalSchedule: InterviewSchedule, updates: ScheduleUpdate[]) {
  const deletedInterviewIds: string[] = [];

  if (!originalSchedule.events) {
    return deletedInterviewIds;
  }

  originalSchedule.events.forEach((originalEvent) => {
    const deletes = (updates || []).filter(
      (update) =>
        update.type === 'ScheduleUpdateDeleteInterview' && update.applicationStageInterviewId === originalEvent.id
    );
    if (deletes.length > 0) {
      deletedInterviewIds.push(originalEvent.id);
    }
  });
  return deletedInterviewIds;
}

export const setScheduleContentStepWaiting =
  (isWaiting: boolean): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.setScheduleContentStepWaiting({ isWaiting }));
  };

export default slice;
